import { useState, useEffect, useContext, useCallback } from "react";
import { AdminChatWindow, ModalMessage } from "../../components";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import {
  clearAllUnreadMsgs,
  setAdminOnlineStatus,
  setRoomHasUnreadMsg,
} from "../../redux/userSlice";
import { SocketContext } from "../../contexts.js/SocketContext";
import { updateStorageUser } from "../../utilities";
import { getAdminActiveChatRoomsApi } from "../../api/chatApi";
import { updateStorageUnreadMessages } from "../../utilities/storageManager";

export default function AdminChats() {
  // admin dashboard for handling chats with users
  // 1 chat window + indicator for new messages per user chat

  const dispatch = useDispatch();

  const { id, isOnline, stayLoggedIn } = useSelector(
    (state) => state.user
  );
  const { socket, createSocket, disconnectSocket } = useContext(SocketContext);

  const [chatRooms, setChatRooms] = useState({}); // {sessionId: { chatHistory: [{user: "msg"}], userName: }}
  const [minimizedChats, setMinimizedChats] = useState([]); //not minimized
  const [newMessagesQueue, setNewMessagesQueue] = useState([]); // [ {message:, sessionId:, ?sender:}]
  const [typingUsers, setTypingUsers] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [showAlertEndChat, setShowAlertEndChat] = useState(false);
  const [showAlertGoOffline, setShowAlertGoOffline] = useState(false);
  const [updateNewMsgAlerts, setUpdateNewMsgAlerts] = useState(new Set());
  const [chatErrorMsg, setChatErrorMsg] = useState(false);

  useEffect(() => {
    if (socket) {
      socket.on("newClientMessage", (data) => {
        setNewMessagesQueue((prevQueue) => [...prevQueue, data]);
      });

      socket.on("partnerIsTyping", (data) => {
        setTypingUsers((prevUsers) => ({
          ...prevUsers,
          [data?.sessionId]: data?.isTyping,
        }));
      });

      socket.on("askAdminIfStillOnline", () => {
        socket.emit("adminConfirmsOnlineStatus", id);
      });

      socket.on("connect_error", () => {
        setChatErrorMsg("We're having trouble connecting to the chat server. Please refresh the page or try again later.")
      })

      socket.on("connect", () => {
        console.log("admin just connected")
        setChatErrorMsg("");
        socket.emit("registerAdmin", id); 
      })
    }
    return () => {
      if (socket) {
        socket.off("newClientMessage");
        socket.off("partnerIsTyping");
        socket.off("askAdminIfStillOnline");
        socket.off("connect_error");
        socket.off("connect");
      }
    };
  }, [socket, id]);

  useEffect(() => {
    const abortController = new AbortController();

    const getChatRooms = async () => {
      try {
        const chatRooms = await getAdminActiveChatRoomsApi(
          id,
          abortController.signal
        );
        return chatRooms;
      } catch (error) {
        setErrorMsg("Could not load chat rooms. Pleas try again later.");
      }
    };

    if (isOnline) {
      getChatRooms()
        .then((res) => {
          setChatRooms(res);
        })
        .catch((error) => {
          setErrorMsg("Could not load chat rooms. Please try again later.");
        });
    }
    return () => {
      abortController.abort();
    };
  }, [isOnline, id]);

  useEffect(() => {
    if (newMessagesQueue.length > 0) {
      // update chatrooms to show new messages
      setChatRooms((prevChatRooms) => {
        let updatedChatRooms = { ...prevChatRooms };
        newMessagesQueue.forEach((data) => {
          const sessionId = data.sessionId;
          if (data.message.user) {
            setUpdateNewMsgAlerts((prevSet) => {
              const updatedSet = new Set(prevSet);
              updatedSet.add(sessionId);
              return updatedSet;
            });
          }
          const existingChat = updatedChatRooms.hasOwnProperty(sessionId);
          if (existingChat) {
            updatedChatRooms[sessionId].chatHistory.push(data?.message);
          } else {
            updatedChatRooms[sessionId] = {
              chatHistory: [data?.message],
              userName: data.userName,
            };
          }
        });

        return updatedChatRooms;
      });
      setNewMessagesQueue([]);
    }
  }, [newMessagesQueue]);
 

  const handleUpdateUnreadMsgs = useCallback((sessionId, hasNewMsg) => {
    updateStorageUnreadMessages(
      { hasNewMessage: hasNewMsg, sessionId },
      stayLoggedIn
    );
    dispatch(setRoomHasUnreadMsg({ hasNewMessage: hasNewMsg, sessionId }));
  }, [stayLoggedIn, dispatch]);


  useEffect(() => {
    if (updateNewMsgAlerts.size > 0) {
      for (const sessionId of updateNewMsgAlerts) {
        handleUpdateUnreadMsgs(sessionId, true);
      }
      setUpdateNewMsgAlerts(new Set());
    }
  }, [updateNewMsgAlerts, handleUpdateUnreadMsgs]);

  const handleConfirmEndChat = () => {
    const sessionId = showAlertEndChat?.payload;
    setChatRooms((prevChatRooms) => {
      let updatedChatRooms = { ...prevChatRooms };
      delete updatedChatRooms[sessionId];
      return updatedChatRooms;
    });
    if (socket) {
      socket.emit("AdminEndsChat", sessionId);
    }
    // remove user from typingUsers
    setTypingUsers((prevUsers) => {
      let updatedUsers = { ...prevUsers };
      delete updatedUsers[sessionId];
      return updatedUsers;
    });
    // remove sessionId from unreadMessages 
    handleUpdateUnreadMsgs(sessionId, false); 
  };

  const handleMinimizeChat = (sessionId) => {
    setMinimizedChats([...minimizedChats, sessionId]);
  };

  const handleMaximizeChat = (sessionId) => {
    setMinimizedChats(minimizedChats.filter((item) => item !== sessionId));
  };

  const submitChatMsg = (message, sessionId) => {
    let messageData = {
      admin: message,
      createdAt: new Date(),
    };
    socket.emit("newAdminMessage", {
      message: messageData,
      sessionId,
    });
    setNewMessagesQueue((prevQueue) => [
      ...prevQueue,
      { sessionId, message: messageData },
    ]);
  };

  const handleToggleOnlineStatus = (status) => {
    // update store and storage
    if (status) {
      // admin going online: open new socket
      createSocket();
      // register new admin with server socket handler
      dispatch(setAdminOnlineStatus({ isOnline: status }, stayLoggedIn));
      updateStorageUser({ isOnline: status }, stayLoggedIn);
    } else {
      // admin goes offline
      setShowAlertGoOffline({ show: true, payload: status });
    }
  };

  const handleConfirmGoOffline = () => {
    const status = showAlertGoOffline?.payload;
    if (socket) {
      socket.emit("adminGoesOffline", id);
      disconnectSocket();
    }
    // update store and storage
    dispatch(setAdminOnlineStatus({ isOnline: status }, stayLoggedIn));
    updateStorageUser({ isOnline: status, unreadMessages: {} }, stayLoggedIn);
    dispatch(clearAllUnreadMsgs()); 
    setChatRooms({});
    setMinimizedChats([]);
    setNewMessagesQueue([]);

  };

  const handleTyping = (isTyping, sessionId) => {
    socket.emit("isTyping", {
      isAdmin: true,
      sessionId,
      isTyping,
    });
  };

  return (
    <Container className="my-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      <div>
        <p>
          Your are currently <b>{isOnline ? "online" : "offline"}</b>. Users{" "}
          {isOnline ? "will" : "won't"} be able to contact you.{" "}
        </p>
        <Button
          onClick={() => handleToggleOnlineStatus(!isOnline)}
          variant={isOnline ? "danger" : "success"}
        >
          {isOnline ? "Go Offline" : "Go Online"}
        </Button>
      </div>

      <Row
        style={{ display: "flex", justifyContent: "space-around" }}
        className="mt-5"
      >
        {chatRooms &&
          Object.keys(chatRooms).map((sessionId) => {
            return (
              <Col
                style={{ position: "relative" }}
                key={sessionId}
                xs={8}
                sm={6}
                md={5}
                xl={4}
                className="mb-5 chat-wrapper-admin"
              >
                <AdminChatWindow
                  sessionId={sessionId}
                  chatData={chatRooms[sessionId]}
                  minimizedChats={minimizedChats}
                  handleMinimizeChat={handleMinimizeChat}
                  handleMaximizeChat={handleMaximizeChat}
                  // handleCloseChat={handleCloseChat}
                  submitChatMsg={submitChatMsg}
                  handleEndChat={(sessionId) =>
                    setShowAlertEndChat({ show: true, payload: sessionId })
                  }
                  handleTyping={handleTyping}
                  partnerIsTyping={typingUsers[sessionId] || false}
                  chatErrorMsg={chatErrorMsg}
                />
              </Col>
            );
          })}
      </Row>
      {/* alert message */}
      {showAlertEndChat?.show && (
        <ModalMessage
          message="Are you sure you want to end this chat?"
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirmEndChat}
          setShowAlertMsg={setShowAlertEndChat}
        />
      )}
      {/* alert message */}
      {showAlertGoOffline?.show && (
        <ModalMessage
          message="Are you sure you want to go offline? All active chats will be closed."
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirmGoOffline}
          setShowAlertMsg={setShowAlertGoOffline}
        />
      )}
    </Container>
  );
}
